import {
  CampaignAnalyticsReport,
  CampaignAnalyticsReportsResponse,
  campaignAnalyticsReportsResponse,
  LocationListExclusion,
  UpdateRequest,
} from './schema';
import { rootApi } from '@components/rootApi';

type GetCampaignAnalyticsReportsParams = {
  campaignId: number | string;
  showExcludedLocations?: boolean;
};

const pathWithBaseUrl = (path: string) => `/api/${path}`;

const campaignAnalyticsApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getCampaignAnalyticsReports: builder.query<
      CampaignAnalyticsReportsResponse,
      GetCampaignAnalyticsReportsParams
    >({
      query: ({ campaignId, ...params }) => ({
        url: pathWithBaseUrl(
          `campaigns/${campaignId}/campaign_analytics_reports`
        ),
        params,
      }),
      transformResponse: (response: CampaignAnalyticsReportsResponse) =>
        campaignAnalyticsReportsResponse.parse(response),
      providesTags: result => {
        return result
          ? result.map(report => ({
              type: 'CampaignAnalyticsReports',
              id: report.id,
            }))
          : [];
      },
    }),
    updateCampaignAnalyticsReport: builder.mutation<
      CampaignAnalyticsReport,
      {
        campaignId: number;
        campaignAnalyticsReportId: number;
        body: UpdateRequest;
      }
    >({
      query: ({ campaignId, campaignAnalyticsReportId, body }) => ({
        url: pathWithBaseUrl(
          `campaigns/${campaignId}/campaign_analytics_reports/${campaignAnalyticsReportId}`
        ),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, __, { campaignAnalyticsReportId }) => [
        { type: 'CampaignAnalyticsReports', id: campaignAnalyticsReportId },
      ],
    }),
    createLocationListExclusion: builder.mutation<
      void,
      Pick<LocationListExclusion, 'locationId' | 'locationListId'>
    >({
      query: body => ({
        url: pathWithBaseUrl('location_list_exclusions'),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LocationListExclusion'],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      async onQueryStarted(params, { dispatch, queryFulfilled, getState }) {
        const { meta } = await queryFulfilled;
        const headers = (meta as { response?: { headers?: Headers } }).response
          ?.headers;
        const locationHeader = headers?.get('Location');
        if (!locationHeader) return;

        const locationListExclusionId = Number(locationHeader.split('/').pop());
        campaignAnalyticsApi.util
          .selectInvalidatedBy(getState(), ['CampaignAnalyticsReports'])
          .forEach(({ endpointName, originalArgs }) => {
            if (endpointName !== 'getCampaignAnalyticsReports') return;

            dispatch(
              campaignAnalyticsApi.util.updateQueryData(
                endpointName,
                // originalArgs is any in the library itself
                originalArgs as GetCampaignAnalyticsReportsParams,
                draft => {
                  draft.forEach(report => {
                    report.brandLocationVisits.data.forEach(
                      brandLocationVisit => {
                        if (
                          brandLocationVisit.locationId === params.locationId &&
                          brandLocationVisit.locationListId ===
                            params.locationListId
                        ) {
                          brandLocationVisit.locationListExclusionId =
                            locationListExclusionId;
                        }
                      }
                    );
                  });
                }
              )
            );
          });
      },
    }),
    deleteLocationListExclusion: builder.mutation<void, number>({
      query: id => ({
        url: pathWithBaseUrl(`location_list_exclusions/${id}`),
        method: 'DELETE',
      }),
      invalidatesTags: ['LocationListExclusion'],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const results = campaignAnalyticsApi.util
          .selectInvalidatedBy(getState(), ['CampaignAnalyticsReports'])
          .filter(
            ({ endpointName }) => endpointName === 'getCampaignAnalyticsReports'
          )
          // TODO: fix ignore
          // eslint-disable-next-line @typescript-eslint/require-await
          .map(async ({ endpointName, originalArgs }) => {
            return dispatch(
              campaignAnalyticsApi.util.updateQueryData(
                endpointName as 'getCampaignAnalyticsReports',
                // originalArgs is any in the library itself
                originalArgs as GetCampaignAnalyticsReportsParams,
                draft => {
                  draft.forEach(report => {
                    report.brandLocationVisits.data.forEach(
                      brandLocationVisit => {
                        if (brandLocationVisit.locationListExclusionId === id) {
                          brandLocationVisit.locationListExclusionId = null;
                        }
                      }
                    );
                  });
                }
              )
            );
          });

        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          // TODO: fix igore
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          results.forEach(async result => {
            const patch = await result;
            patch.undo();
          });
        }
      },
    }),
  }),
});

export const {
  useGetCampaignAnalyticsReportsQuery,
  useUpdateCampaignAnalyticsReportMutation,
  useCreateLocationListExclusionMutation,
  useDeleteLocationListExclusionMutation,
} = campaignAnalyticsApi;
